import React, { useEffect, useRef, useState } from "react";

import countriesData from "../../data/countriesList.json";

import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  ListItemIcon,
  FormControl,
} from "@mui/material";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SaveIcon from "@mui/icons-material/Save";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CountryFlag from "react-country-flag";

import { colors } from "@components/ui/colors";

import { useForm, Controller } from "react-hook-form";

import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";

import { EditableCompanyAvatar } from "./CompanyAvatar";

import { selectCurrentCompany } from "src/redux/features/user/selectors/userState.selectors";

import { useSelector } from "react-redux";

import { useUpdateCompanyInfo } from "./CompanyAvatar/hooks/useUpdateCompanyInfo";

import { UpdateCompanyPayload } from "./CompanyAvatar/actions/UpdateCompanyPayload";

import { SnackbarAlert } from "./SnackbarAlert";

/* =========================================================
   TYPES
========================================================= */

type Country = {
  code?: string;
  dialCode?: string;
  name?: string;
  file_url?: string;

  // Compatibility with old JSON structure
  alpha3?: string;
  numberPrefix?: string;
};

/* =========================================================
   COUNTRIES
========================================================= */

const countries = countriesData as unknown as Country[];

/* =========================================================
   INPUT STYLE
========================================================= */

const orangeInputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px",
    backgroundColor: "#ffffff",

    "& fieldset": {
      borderColor: colors.orange.base,
    },

    "&:hover fieldset": {
      borderColor: colors.orange[300],
    },

    "&.Mui-focused fieldset": {
      borderColor: colors.orange.base,
      borderWidth: "2px",
    },
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: colors.orange.base,
  },
};

/* =========================================================
   ADDRESS PREDICTIVE
========================================================= */

const AddressPredictive = ({
  value,
  onAddressSelect,
}: {
  value: string;

  onAddressSelect: (data: { state: string; zipCode: string; country: string; fullAddress: string }) => void;
}) => {
  const [address, setAddress] = useState(value || "");
  const [loading, setLoading] = useState(false);

  /* =======================================================
     SYNC ADDRESS WITH REACT HOOK FORM
  ======================================================= */

  useEffect(() => {
    setAddress(value || "");
  }, [value]);

  /* =======================================================
     PARSE GOOGLE ADDRESS
  ======================================================= */

  const parseAddress = async (googleAddress: string) => {
    try {
      setLoading(true);

      const results = await geocodeByAddress(googleAddress);

      if (!results || results.length === 0) {
        return;
      }

      const addressComponents = results[0]?.address_components || [];

      const getAddressComponent = (type: string) => {
        return addressComponents.find((component) => component.types?.includes(type))?.long_name || "";
      };

      /* STATE */

      const state = getAddressComponent("administrative_area_level_1");

      /* ZIP CODE */

      const zipCode = getAddressComponent("postal_code");

      /* COUNTRY */

      const country = getAddressComponent("country");

      /*
       * IMPORTANT:
       * We use exactly the address selected
       * by Google.
       */

      const fullAddress = googleAddress;

      setAddress(fullAddress);

      onAddressSelect({
        state,
        zipCode,
        country,
        fullAddress,
      });
    } catch (error) {
      console.error("Error parsing address:", error);
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
      }}
    >
      <PlacesAutocomplete
        value={address}
        onChange={(value) => {
          setAddress(value);
        }}
        onSelect={parseAddress}
      >
        {({ getInputProps, suggestions = [], getSuggestionItemProps, loading: placesLoading }) => (
          <Box>
            {/* ADDRESS INPUT */}

            <TextField
              {...getInputProps({
                label: "Address",
                placeholder: "Enter street address, city, or postal code",
                variant: "outlined",
                size: "small",
                fullWidth: true,
              })}
              sx={orangeInputStyle}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon
                      sx={{
                        color: colors.orange.base,
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {/* LOADING */}

            {(placesLoading || loading) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  p: 1.5,
                }}
              >
                <CircularProgress
                  size={20}
                  sx={{
                    color: colors.orange.base,
                  }}
                />
              </Box>
            )}

            {/* GOOGLE SUGGESTIONS */}

            {suggestions.length > 0 && !placesLoading && !loading && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 0.5,
                  borderRadius: "8px",
                  border: `1px solid ${colors.orange[200]}`,
                  overflow: "hidden",
                  maxHeight: "220px",
                  overflowY: "auto",
                }}
              >
                <List sx={{ p: 0 }}>
                  {suggestions.map((suggestion, index) => (
                    <ListItemButton
                      key={suggestion.placeId || index}
                      {...getSuggestionItemProps(suggestion)}
                      sx={{
                        py: 1.5,
                        px: 2,

                        borderBottom: index !== suggestions.length - 1 ? "1px solid #f1f5f9" : "none",

                        transition: "all 0.2s ease",

                        "&:hover": {
                          backgroundColor: colors.orange[50],
                          pl: 2.5,
                        },
                      }}
                    >
                      <ListItemText
                        primary={suggestion.description}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "#334155",
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        )}
      </PlacesAutocomplete>
    </Box>
  );
};

/* =========================================================
   SETTINGS COMPANY
========================================================= */

export const SettingsCompany = () => {
  /* =======================================================
     MENU / AVATAR
  ======================================================= */

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const [isAvatarLibraryOpen, setIsAvatarLibraryOpen] = useState(false);

  /* =======================================================
     COUNTRY CODE
  ======================================================= */

  const initializedCompanyId = useRef<string | number | null>(null);

  const [isCodesOpen, setIsCodesOpen] = useState(false);

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  /* =======================================================
     UPDATE COMPANY
  ======================================================= */

  const { mutate: updateCompany, isPending } = useUpdateCompanyInfo();

  /* =======================================================
     CURRENT COMPANY
  ======================================================= */

  const currentCompany = useSelector(selectCurrentCompany) as any;

  const currentCompanyData = currentCompany || {};

  /* =======================================================
     GET COUNTRY CODE FROM COUNTRY NAME
     
     Mexico  -> MX-+52
     Peru    -> PE-+51
     Bolivia -> BO-+591
  ======================================================= */

  const getCountryCodeFromCountryName = (countryName?: string) => {
    if (!countryName) {
      return "";
    }

    const normalizedCountryName = countryName.trim().toLowerCase();

    const country = countries.find((item) => item.name?.trim().toLowerCase() === normalizedCountryName);

    if (!country?.code || !country?.dialCode) {
      return "";
    }

    return `${country.code}-${country.dialCode}`;
  };

  const getCountryCodeFromDialCode = (dialCode?: string) => {
    if (!dialCode) {
      return "US-+1";
    }

    const normalizedDialCode = String(dialCode).trim();
    const country = countries.find((item) => item.dialCode === normalizedDialCode);

    if (!country?.code || !country?.dialCode) {
      return "US-+1";
    }

    return `${country.code}-${country.dialCode}`;
  };

  /* =======================================================
     GET SELECTED COUNTRY
  ======================================================= */

  const getSelectedCountry = (selectedValue?: string) => {
    if (!selectedValue) {
      return undefined;
    }

    const value = String(selectedValue);

    const separatorIndex = value.indexOf("-");

    if (separatorIndex === -1) {
      return countries.find((country) => country.code === value);
    }

    const code = value.slice(0, separatorIndex);

    return countries.find((country) => country.code === code);
  };

  /* =======================================================
     REACT HOOK FORM
  ======================================================= */

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<UpdateCompanyPayload>({
    defaultValues: {
      companyName: currentCompanyData?.businessName || "",

      email: currentCompanyData?.businessEmail || currentCompanyData?.email || "",

      phone: currentCompanyData?.businessPhone || "",

      address: currentCompanyData?.address || "",

      state: currentCompanyData?.state || "",

      zipCode: currentCompanyData?.zipCode || "",

      country: currentCompanyData?.country || "",

      countryCode: currentCompanyData?.countryCode || "US",

      dialCode: "+1",
    },
  });

  /* =======================================================
     RESET WHEN COMPANY CHANGES
  ======================================================= */

  useEffect(() => {
    const companyId = currentCompanyData?.id;

    if (!companyId || initializedCompanyId.current === companyId) {
      return;
    }

    initializedCompanyId.current = companyId;

    const countryFromAddress = getCountryCodeFromCountryName(currentCompanyData?.country);

    const detectedDialCode = currentCompanyData?.dialCode || (countryFromAddress ? countryFromAddress.slice(countryFromAddress.indexOf("-") + 1) : "+1");
    const countryFromDialCode = getCountryCodeFromDialCode(detectedDialCode);
    const detectedCode = currentCompanyData?.countryCode || countryFromDialCode.split("-")[0] || countryFromAddress.split("-")[0] || "US";

    reset({
      companyName: currentCompanyData?.businessName || "",
      email: currentCompanyData?.businessEmail || currentCompanyData?.email || "",
      phone: currentCompanyData?.businessPhone || "",
      address: currentCompanyData?.address || "",
      state: currentCompanyData?.state || "",
      zipCode: currentCompanyData?.zipCode || "",
      country: currentCompanyData?.country || "",
      countryCode: detectedCode,
      dialCode: detectedDialCode,
    });
  }, [currentCompanyData, reset]);
  /* =======================================================
     SUBMIT
  ======================================================= */

  const onSubmit = (data: UpdateCompanyPayload) => {
    console.log("=================================");

    console.log("UPDATE COMPANY PAYLOAD");

    console.log("=================================");

    console.log("COMPANY:", data.companyName);

    console.log("EMAIL:", data.email);

    console.log("PHONE:", data.phone);

    console.log("DIAL CODE:", data.dialCode);

    console.log("ADDRESS:", data.address);

    console.log("STATE:", data.state);

    console.log("ZIP:", data.zipCode);

    console.log("COUNTRY:", data.country);

    console.log("FULL FORM DATA:", data);

    updateCompany(data, {
      onSuccess: () => {
        setSnackbarState({
          open: true,
          message: "Company details updated successfully!",
          severity: "success",
        });
      },

      onError: (error: any) => {
        console.error("UPDATE COMPANY ERROR:", error);

        console.error("BACKEND RESPONSE:", error?.response?.data);

        setSnackbarState({
          open: true,
          message: error?.response?.data?.message || "Failed to update company details.",
          severity: "error",
        });
      },
    });
  };

  /* =======================================================
     PHONE FORMAT
  ======================================================= */

  const formatPhone = (value?: string) => {
    const digits = value?.replace(/\D/g, "") || "";

    if (!digits) {
      return "";
    }

    if (digits.length <= 3) {
      return `(${digits}`;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        my: 5,
      }}
    >
      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <SnackbarAlert
        openState={snackbarState.open}
        setOpenState={(open: boolean) =>
          setSnackbarState((prev) => ({
            ...prev,
            open,
          }))
        }
        message={snackbarState.message}
        severity={snackbarState.severity}
        position="bottom-right"
      />

      {/* ===================================================
          FORM
      =================================================== */}

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          width: "100%",
          minWidth: "600px",
          px: 6,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* =================================================
            COMPANY DETAILS
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "none",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: "#1e293b",
              mb: 3,
              letterSpacing: 0.5,
            }}
          >
            COMPANY DETAILS
          </Typography>

          <Grid container spacing={3} alignItems="center">
            {/* AVATAR */}

            <Grid item xs={12} sm={4} display="flex" flexDirection="column" alignItems="center">
              <Box
                sx={{
                  position: "relative",
                  mb: 1,
                }}
              >
                <EditableCompanyAvatar
                  company={currentCompany}
                  size={120}
                  onOpenLibrary={handleCloseMenu}
                  onLibraryOpenChange={setIsAvatarLibraryOpen}
                />
              </Box>
            </Grid>

            {/* COMPANY NAME / EMAIL */}

            <Grid item xs={12} sm={8}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Controller
                  name="companyName"
                  control={control}
                  rules={{
                    required: "Name is required",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Company Name"
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                      sx={orangeInputStyle}
                    />
                  )}
                />
              </Box>

              <Box sx={{ pr: 4 }}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",

                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

                      message: "Invalid email format",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Verified Email Address">
                              <HelpOutlineIcon
                                sx={{
                                  color: "#f97316",
                                  cursor: "pointer",
                                  fontSize: 20,
                                }}
                              />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* =================================================
            CONTACT INFORMATION
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "none",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: "#1e293b",
              mb: 3,
              letterSpacing: 0.5,
            }}
          >
            CONTACT INFORMATION
          </Typography>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              mt: {
                xs: 0.5,
                md: 0,
              },
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            {/* =================================================
                COUNTRY CODE
            ================================================= */}

            <Controller
              name="dialCode"
              control={control}
              render={({ field }) => (
                <FormControl
                  variant="outlined"
                  sx={{
                    width: "auto",
                    minWidth: 110,
                    mr: 1,
                  }}
                >
                  <Select
                    value={watch("countryCode") && field.value ? `${watch("countryCode")}-${field.value}` : "US-+1"}
                    onChange={(event) => {
                      const value = String(event.target.value || "");

                      const separatorIndex = value.indexOf("-");

                      const selectedCountryCode = separatorIndex !== -1 ? value.slice(0, separatorIndex) : value;

                      const dialCode = separatorIndex !== -1 ? value.slice(separatorIndex + 1) : "";

                      setValue("countryCode", selectedCountryCode, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });

                      field.onChange(dialCode);

                      setValue("dialCode", dialCode, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });

                      console.log("SELECTED COUNTRY:", selectedCountryCode);
                      console.log("SELECTED DIAL CODE:", dialCode);
                    }}
                    onOpen={() => setIsCodesOpen(true)}
                    onClose={() => setIsCodesOpen(false)}
                    variant="outlined"
                    fullWidth
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      const selectedValue = selected ? String(selected) : "";

                      const selectedCountry = getSelectedCountry(selectedValue);

                      if (!selectedCountry) {
                        return (
                          <Box
                            sx={{
                              color: "#64748b",
                            }}
                          >
                            Code
                          </Box>
                        );
                      }

                      const countryCodeValue = selectedCountry.code || "";

                      const dialCode = selectedCountry.dialCode || "";

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            minHeight: 24,
                          }}
                        >
                          {/* FLAG */}

                          <CountryFlag
                            svg
                            countryCode={countryCodeValue}
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "4px",
                              objectFit: "cover",
                              display: "block",
                              flexShrink: 0,
                            }}
                            aria-label={selectedCountry.name || countryCodeValue}
                          />

                          {/* DIAL CODE */}

                          <Box
                            component="span"
                            sx={{
                              lineHeight: 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dialCode}
                          </Box>
                        </Box>
                      );
                    }}
                    sx={{
                      borderRadius: "12px",

                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        minHeight: "40px !important",
                        boxSizing: "border-box",
                      },

                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.orange.base,
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.orange[600],
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.orange.base,
                        borderWidth: "2px",
                      },
                    }}
                  >
                    {countries
                      .filter((country) => country.code && country.dialCode && country.name)
                      .map((country) => (
                        <MenuItem key={`${country.code}-${country.dialCode}`} value={`${country.code}-${country.dialCode}`}>
                          {/* FLAG */}

                          <ListItemIcon
                            sx={{
                              minWidth: "36px",
                            }}
                          >
                            <CountryFlag
                              svg
                              countryCode={country.code!}
                              style={{
                                width: "22px",
                                height: "22px",
                                borderRadius: "4px",
                                objectFit: "cover",
                                display: "block",
                              }}
                              aria-label={country.name}
                            />
                          </ListItemIcon>

                          {/* COUNTRY */}

                          <ListItemText primary={isCodesOpen ? `${country.name} (${country.dialCode})` : country.dialCode} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />

            {/* =================================================
                PHONE
            ================================================= */}

            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",

                validate: (value) => {
                  const digits = value?.replace(/\D/g, "") || "";

                  return digits.length === 10 || "Phone number must be 10 digits";
                },
              }}
              render={({ field }) => {
                const formattedPhone = formatPhone(field.value);

                return (
                  <TextField
                    {...field}
                    id="phone"
                    type="tel"
                    label="Phone Number"
                    size="small"
                    value={formattedPhone}
                    onChange={(event) => {
                      const onlyNumbers = event.target.value.replace(/\D/g, "");

                      field.onChange(onlyNumbers.slice(0, 10));
                    }}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    variant="outlined"
                    inputProps={{
                      maxLength: 14,
                    }}
                    sx={{
                      width: "100%",
                      ...orangeInputStyle,
                    }}
                  />
                );
              }}
            />
          </Box>
        </Paper>

        {/* =================================================
            ADDRESS INFORMATION
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "none",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: "#1e293b",
              mb: 3,
              letterSpacing: 0.5,
            }}
          >
            ADDRESS INFORMATION
          </Typography>

          <Grid container spacing={3}>
            {/* =================================================
                FULL ADDRESS SEARCH
            ================================================= */}

            <Grid item xs={12}>
              <AddressPredictive
                value={watch("address") || ""}
                onAddressSelect={({ state, zipCode, country, fullAddress }) => {
                  /* ADDRESS */

                  setValue("address", fullAddress, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });

                  /* STATE */

                  setValue("state", state, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });

                  /* ZIP CODE */

                  if (zipCode) {
                    setValue("zipCode", zipCode, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }

                  /* COUNTRY */

                  setValue("country", country, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });

                  /* =================================================
                     COUNTRY CODE
                  ================================================= */

                  const detectedCountryCode = getCountryCodeFromCountryName(country);

                  if (detectedCountryCode) {
                    const separatorIndex = detectedCountryCode.indexOf("-");

                    const selectedCountryCode = detectedCountryCode.slice(0, separatorIndex);

                    setValue("countryCode", selectedCountryCode, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });

                    let dialCode = detectedCountryCode;

                    if (separatorIndex !== -1) {
                      dialCode = detectedCountryCode.slice(separatorIndex + 1);
                    }

                    setValue("dialCode", dialCode, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
              />
            </Grid>

            {/* STATE */}

            <Grid item xs={12} sm={6}>
              <Controller
                name="state"
                control={control}
                rules={{
                  required: "State is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="State"
                    error={!!errors.state}
                    helperText={errors.state?.message}
                    sx={orangeInputStyle}
                  />
                )}
              />
            </Grid>

            {/* ZIP CODE */}

            <Grid item xs={12} sm={6}>
              <Controller
                name="zipCode"
                control={control}
                rules={{
                  required: "Zip code is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Zip Code"
                    error={!!errors.zipCode}
                    helperText={errors.zipCode?.message}
                    sx={orangeInputStyle}
                  />
                )}
              />
            </Grid>

            {/* COUNTRY */}

            <Grid item xs={12}>
              <Controller
                name="country"
                control={control}
                rules={{
                  required: "Country is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Country"
                    error={!!errors.country}
                    helperText={errors.country?.message}
                    sx={orangeInputStyle}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* =================================================
            SAVE BUTTON
        ================================================= */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              backgroundColor: colors.orange.base,

              "&:hover": {
                background: colors.orange[600],
              },

              textTransform: "none",

              borderRadius: "10px",

              px: 4,
              py: 1,

              fontWeight: 700,
            }}
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsCompany;
